import { fabric } from 'fabric';
import { cloneFabricJson, getWorkspaceSizeFromFabricJson } from '@/image-designer/utils/slideModel';
import { refreshAllStampLayerVisuals } from '@/image-designer/utils/stampLayerUtils';
import { syncAllStampPreviews } from '@/image-designer/utils/stampPreviewSync';
import {
  collectFabricJsonImageSrcUrls,
  getIpeLayersForSlide,
  slideUsesFabricComposite,
} from '@/image-designer/adapters/ipeLayerAdapter';
import {
  applyCompositeImagesToFabricJson,
  collectStampExampleUrlsFromFabricJson,
  isStampFabricObject,
} from '@/image-designer/adapters/stampAreasAdapter';
import {
  findTemplateBaseInFabricJson,
  isTemplateBaseObject,
} from '@/image-designer/utils/slideImageUtils';
import { isCheckerboardFill } from '@/image-designer/utils/checkerboardPattern';
import { resolveDrawableImageUrl } from '@/pages/ipe/workbench/utils/goodsTemplateImageUrls';
import { ensureDisplayImageUrls } from '@/utils/imageDisplayAccess';
import { resolveFetchableImageUrl } from '@/utils/imageUrlUtils';
import { resolveStudioPreviewUrl } from '@/pages/ipe/workbench/utils/studioImagePreview';

const EXPORT_FORMAT = 'jpeg';
const EXPORT_QUALITY = 0.95;

function isStampOrPreviewObject(obj) {
  if (!obj || obj.id === 'workspace') return true;
  if (isStampFabricObject(obj)) return true;
  if (obj?.linkData?.role === 'stampPreview') return true;
  if (typeof obj?.id === 'string' && obj.id.startsWith('stamp-preview-')) return true;
  return false;
}

function collectFabricTemplateBaseCandidateSrcs(fabricJson, slide = {}) {
  const candidates = [];
  const push = (raw) => {
    const s = String(raw || '').trim();
    if (s && !candidates.includes(s)) candidates.push(s);
  };
  push(slide.imageUrl);
  push(slide.thumbUrl);
  push(slide.url);
  push(slide.src);

  const objects = fabricJson?.objects || [];
  const base = findTemplateBaseInFabricJson(fabricJson);
  if (base?.type === 'image') push(base.src);
  objects.forEach((obj) => {
    if (obj?.type !== 'image') return;
    if (isStampOrPreviewObject(obj)) return;
    push(obj.src);
  });
  return candidates;
}

function neutralizeWorkspaceFillForExport(fabricJson) {
  const cloned = cloneFabricJson(fabricJson);
  const workspace = (cloned.objects || []).find((o) => o.id === 'workspace');
  if (!workspace) return cloned;
  // 棋盘格是设计器透明预览用的 Pattern；合成导出必须去掉，否则成品会带棋盘格
  if (isCheckerboardFill(workspace.fill) || workspace.fill == null) {
    workspace.fill = '#ffffff';
  }
  if (workspace.stroke) {
    workspace.strokeWidth = 0;
  }
  return cloned;
}

/**
 * 同步：用已解析的底图 URL 写入 templateBase（禁止走 ensureDesignerBackgroundLayer，
 * 那条路径在 signed-only 下会把 /uploads/ 底图 src 清空）。
 */
export function ensureFabricSlideTemplateBaseForComposite(fabricJson, slide = {}) {
  const src = String(slide.imageUrl || slide.thumbUrl || '').trim();
  const json = neutralizeWorkspaceFillForExport(fabricJson);
  const objects = [...(json.objects || [])];
  const workspace = objects.find((o) => o.id === 'workspace');
  const wsW = workspace?.width || Number(slide.width) || 900;
  const wsH = workspace?.height || Number(slide.height) || 1200;

  let base = findTemplateBaseInFabricJson({ ...json, objects });
  if (!base) {
    base = objects.find((o) => o.type === 'image' && !isStampOrPreviewObject(o)) || null;
  }

  if (base && base.type === 'image') {
    if (src) base.src = src;
    base.linkData = { ...(base.linkData || {}), role: 'templateBase' };
    base.visible = true;
    base.opacity = 1;
    // 保证底图在 workspace 之上、印花之下
    const baseIdx = objects.indexOf(base);
    const wi = objects.findIndex((o) => o.id === 'workspace');
    if (baseIdx >= 0 && wi >= 0 && baseIdx !== wi + 1) {
      objects.splice(baseIdx, 1);
      objects.splice(wi + 1, 0, base);
    }
    return { ...json, objects };
  }

  if (!src) return { ...json, objects };

  const imgW = Number(slide.width) || wsW;
  const imgH = Number(slide.height) || wsH;
  // cover 铺满，避免注入底图时画布留白 → JPG 白边
  const scale = Math.max(wsW / imgW, wsH / imgH) || 1;
  const imageLayer = {
    type: 'image',
    id: slide.id || `template-base-${Date.now()}`,
    name: slide.name || '商品主图',
    src,
    left: (workspace?.left || 0) + wsW / 2,
    top: (workspace?.top || 0) + wsH / 2,
    originX: 'center',
    originY: 'center',
    width: imgW,
    height: imgH,
    scaleX: scale,
    scaleY: scale,
    visible: true,
    opacity: 1,
    linkData: { role: 'templateBase' },
  };

  const placeholderIdx = objects.findIndex(
    (o) => isTemplateBaseObject(o) && o.type === 'rect',
  );
  if (placeholderIdx >= 0) {
    objects.splice(placeholderIdx, 1, imageLayer);
  } else {
    const wi = objects.findIndex((o) => o.id === 'workspace');
    if (wi >= 0) objects.splice(wi + 1, 0, imageLayer);
    else objects.unshift(imageLayer);
  }
  return { ...json, objects };
}

/**
 * 合成前解析商品主图为可绘制 data URL，再写入 fabric（保留/补全 templateBase）。
 */
export async function prepareFabricSlideTemplateBaseForComposite(fabricJson, slide = {}) {
  const candidates = collectFabricTemplateBaseCandidateSrcs(fabricJson, slide);
  let drawable = '';
  for (const candidate of candidates) {
    if (candidate.startsWith('data:image/')) {
      drawable = candidate;
      break;
    }
    drawable = await resolveDrawableImageUrl(candidate, { imageId: slide.id });
    if (drawable?.startsWith?.('data:image/')) break;
    drawable = '';
  }

  // data URL 优先；否则至少保留已签名/可 fetch 地址，再交 resolveFabricJsonForRender
  let fallbackSrc = '';
  if (!drawable) {
    for (const candidate of candidates) {
      if (!candidate || candidate.startsWith('data:')) continue;
      await ensureDisplayImageUrls([candidate]);
      const signed = await resolveFetchableImageUrl(candidate, { id: slide.id });
      if (signed) {
        fallbackSrc = signed;
        break;
      }
      fallbackSrc = candidate;
      break;
    }
  }

  const resolvedSrc = drawable || fallbackSrc || '';
  if (!resolvedSrc) {
    const existingBase = findTemplateBaseInFabricJson(fabricJson);
    const existingSrc = String(existingBase?.src || '').trim();
    if (!existingSrc) {
      throw new Error(
        `商品底图无法加载（slide=${slide.id || 'unknown'}），已中止合成以免生成白板图`,
      );
    }
    console.warn(
      '[主图创作] 未能重新解析底图，沿用 fabric 内已有 templateBase.src',
      { slideId: slide.id, src: existingSrc.slice(0, 80) },
    );
    return ensureFabricSlideTemplateBaseForComposite(fabricJson, {
      ...slide,
      imageUrl: existingSrc,
      thumbUrl: existingSrc,
    });
  }

  return ensureFabricSlideTemplateBaseForComposite(fabricJson, {
    ...slide,
    imageUrl: resolvedSrc,
    thumbUrl: resolvedSrc,
  });
}

/** loadFromJSON 前批量 sign-batch 并替换 obj.src / 印花 exampleImageUrl，避免 signed-only 裸拉 /uploads/ */
async function resolveFabricJsonForRender(fabricJson) {
  const cloned = cloneFabricJson(fabricJson);
  if (!cloned?.objects?.length) return cloned;

  const rawUrls = [
    ...new Set([
      ...collectFabricJsonImageSrcUrls(cloned),
      ...collectStampExampleUrlsFromFabricJson(cloned),
    ]),
  ];
  if (rawUrls.length > 0) {
    await ensureDisplayImageUrls(rawUrls);
  }

  for (const obj of cloned.objects) {
    if (obj?.type === 'image') {
      const raw = String(obj.src || '').trim();
      if (raw && !raw.startsWith('data:') && !raw.startsWith('blob:')) {
        obj.src = await resolveFetchableImageUrl(raw, obj.linkData || {});
      }
    }
    if (isStampFabricObject(obj) && obj.linkData?.exampleImageUrl) {
      const raw = String(obj.linkData.exampleImageUrl || '').trim();
      if (raw && !raw.startsWith('data:') && !raw.startsWith('blob:')) {
        const linkData = obj.linkData || {};
        obj.linkData = {
          ...linkData,
          exampleImageUrl: await resolveStudioPreviewUrl(raw, {
            displayUrl: linkData.exampleImageDisplayUrl,
            viewUrl: linkData.exampleImageViewUrl,
          }),
        };
      }
    }
  }

  return cloned;
}

/**
 * 等待 StaticCanvas 上所有图片元素加载完成
 * @param {fabric.StaticCanvas} canvas
 */
function waitForCanvasImages(canvas) {
  const objects = canvas.getObjects().filter((o) => o.type === 'image');
  if (objects.length === 0) return Promise.resolve();

  return Promise.all(
    objects.map(
      (obj) =>
        new Promise((resolve) => {
          if (obj.visible === false || obj.opacity === 0) {
            resolve();
            return;
          }
          const el = obj.getElement?.();
          if (!el) {
            // loadFromJSON 后偶发尚未挂上 element，短延时再查一次
            window.setTimeout(() => {
              const retryEl = obj.getElement?.();
              if (!retryEl) {
                resolve();
                return;
              }
              if (retryEl.complete && retryEl.naturalWidth > 0) {
                resolve();
                return;
              }
              const onDone = () => {
                retryEl.removeEventListener('load', onDone);
                retryEl.removeEventListener('error', onDone);
                resolve();
              };
              retryEl.addEventListener('load', onDone);
              retryEl.addEventListener('error', onDone);
            }, 0);
            return;
          }
          if (el.complete && el.naturalWidth > 0) {
            resolve();
            return;
          }
          const onDone = () => {
            el.removeEventListener('load', onDone);
            el.removeEventListener('error', onDone);
            resolve();
          };
          el.addEventListener('load', onDone);
          el.addEventListener('error', onDone);
        }),
    ),
  );
}

function orderWorkspaceAndTemplateBase(canvas) {
  const objs = canvas.getObjects();
  const workspace = objs.find((o) => o.id === 'workspace');
  const base = objs.find((o) => isTemplateBaseObject(o) || (o.type === 'image' && o.linkData?.role === 'templateBase'));
  if (workspace && typeof workspace.sendToBack === 'function') {
    workspace.sendToBack();
  }
  if (base && workspace && typeof canvas.moveTo === 'function') {
    const wi = canvas.getObjects().indexOf(workspace);
    canvas.moveTo(base, Math.max(0, wi + 1));
  }
}

/**
 * 将 fabric JSON 离屏渲染为 data URL
 * @param {object} fabricJson
 * @param {{ format?: string, quality?: number }} [options]
 * @returns {Promise<string>}
 */
export function renderFabricJsonToDataUrl(fabricJson, options = {}) {
  const preparedJson = neutralizeWorkspaceFillForExport(fabricJson);
  const { width, height } = getWorkspaceSizeFromFabricJson(preparedJson);
  const format = options.format || EXPORT_FORMAT;
  const quality = options.quality ?? EXPORT_QUALITY;
  const requireTemplateBase = options.requireTemplateBase === true;

  return resolveFabricJsonForRender(preparedJson).then((signedJson) => new Promise((resolve, reject) => {
    let canvas = null;
    try {
      canvas = new fabric.StaticCanvas(null, { width, height, backgroundColor: '#ffffff' });
    } catch (e) {
      reject(e);
      return;
    }

    const jsonStr = JSON.stringify(signedJson);
    canvas.loadFromJSON(jsonStr, async () => {
      try {
        orderWorkspaceAndTemplateBase(canvas);
        refreshAllStampLayerVisuals(canvas, fabric);
        await syncAllStampPreviews(canvas, fabric);
        orderWorkspaceAndTemplateBase(canvas);
        canvas.renderAll();
        await waitForCanvasImages(canvas);
        orderWorkspaceAndTemplateBase(canvas);
        canvas.renderAll();

        const base = canvas.getObjects().find((o) => isTemplateBaseObject(o));
        const baseEl = base?.type === 'image' ? base.getElement?.() : null;
        if (requireTemplateBase) {
          if (!base || !baseEl || !(baseEl.naturalWidth > 0)) {
            canvas?.dispose?.();
            reject(new Error('商品底图加载失败，已中止导出以免生成白板图'));
            return;
          }
        } else if (base && (!baseEl || !(baseEl.naturalWidth > 0))) {
          console.warn('[主图创作] templateBase 图片未加载成功', {
            src: String(base.src || '').slice(0, 120),
            hasElement: Boolean(baseEl),
            naturalWidth: baseEl?.naturalWidth || 0,
          });
        }

        const dataUrl = canvas.toDataURL({
          format,
          quality,
          multiplier: 1,
        });

        canvas.dispose();
        resolve(dataUrl);
      } catch (err) {
        canvas?.dispose?.();
        reject(err);
      }
    });
  }));
}

/**
 * 单页套图：替换 ipelayer 图层并渲染整页
 * @param {object} fabricJson 模板 slide fabric JSON
 * @param {Record<string, string>} imageGroup 图层名 → 图片 id
 * @param {Record<string, string>} imageUrlMap 图片 id → 可绘制 URL
 * @returns {Promise<{ compositeUrl: string, fabricInstanceJson: object }>}
 */
export async function compositeFabricSlideToDataUrl(fabricJson, imageGroup, imageUrlMap, slideMeta = null) {
  const withBase = slideMeta
    ? await prepareFabricSlideTemplateBaseForComposite(fabricJson, slideMeta)
    : fabricJson;
  const prepared = applyCompositeImagesToFabricJson(withBase, imageGroup, imageUrlMap);
  const compositeUrl = await renderFabricJsonToDataUrl(prepared, { requireTemplateBase: true });
  return {
    compositeUrl,
    fabricInstanceJson: prepared,
  };
}

/**
 * 模板多页批量合成（单组 imageGroup）
 * @param {object} templateData
 * @param {Record<string, string>} imageGroup
 * @param {Record<string, string>} imageUrlMap
 * @param {Array<{ id: string }>} [templateImages]
 */
export async function compositeAllFabricSlides(
  templateData,
  imageGroup,
  imageUrlMap,
  templateImages = [],
) {
  const images = templateImages.length > 0
    ? templateImages
    : (templateData.images || []);

  const results = [];

  for (const img of images) {
    const slideId = img.id;
    if (!slideUsesFabricComposite(slideId, templateData)) continue;

    const slideCanvas =
      templateData.slideCanvases?.[slideId]
      || (templateData.primaryImageId === slideId ? templateData.fabricCanvas : null);

    if (!slideCanvas) continue;

    const ipeLayers = getIpeLayersForSlide(slideId, templateData);
    const required = new Set(ipeLayers.map((l) => l.name));
    const hasAll = [...required].every((name) => imageGroup[name]);
    if (!hasAll) continue;

    try {
      const slideMeta = {
        id: slideId,
        name: img.name || slideId,
        width: img.width,
        height: img.height,
        imageUrl: img.url || img.src || img.imageUrl || '',
        thumbUrl: img.thumbUrl || '',
      };
      const { compositeUrl, fabricInstanceJson } = await compositeFabricSlideToDataUrl(
        slideCanvas,
        imageGroup,
        imageUrlMap,
        slideMeta,
      );
      if (compositeUrl) {
        results.push({
          slideId,
          imageId: slideId,
          compositeUrl,
          fabricInstanceJson,
        });
      }
      await new Promise((r) => setTimeout(r, 0));
    } catch (e) {
      console.warn('[fabricCanvasComposite] slide composite error', slideId, e);
    }
  }

  return results;
}

export { slideUsesFabricComposite, getIpeLayersForSlide };
